import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MessageSquare, ShieldCheck } from 'lucide-react';
import { get, post } from '@/lib/api';
import { useBranchLock } from '@/lib/branch';
import { describeError } from '@/lib/auth';
import { FormActions, FormScreen, FormSection } from '@/components/ui/form-screen';
import { Alert, Checkbox, Field, Input, Select, Textarea } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { safeReturnTo } from '../opd/BookAppointmentPage';
import { AUTHORITY_KIND_INFO, type AuthorityKind } from './accounts-types';
import { parseAmountMinor, parsePercent } from './accounts-api';

export interface ChangeTarget {
  userId?: string | null;
  userIds?: string[];
  roleId?: string | null;
  branchId?: string | null;
  title: string;
}

export function authorityChangePath(target: ChangeTarget, kinds: AuthorityKind[], returnTo: string, withBranches = false): string {
  const query = new URLSearchParams({ kinds: kinds.join(','), title: target.title, returnTo });
  if (target.userId) query.set('userId', target.userId);
  if (target.userIds && target.userIds.length > 0) query.set('userIds', target.userIds.join(','));
  if (target.roleId) query.set('roleId', target.roleId);
  if (target.branchId) query.set('branchId', target.branchId);
  if (withBranches) query.set('branches', '1');
  return `/accounts/authority/changes/new?${query.toString()}`;
}

export function AuthorityChangePage() {
  const [params] = useSearchParams();
  const returnTo = safeReturnTo(params.get('returnTo'), '/accounts/authority');
  const kinds = (params.get('kinds') ?? '').split(',').filter((kind): kind is AuthorityKind => kind in AUTHORITY_KIND_INFO);
  const target: ChangeTarget = {
    userId: params.get('userId'),
    userIds: (params.get('userIds') ?? '').split(',').filter(Boolean),
    roleId: params.get('roleId'),
    branchId: params.get('branchId'),
    title: params.get('title') ?? tr('billing', 'financial_authority'),
  };
  const wantsBranches = params.get('branches') === '1';
  const branches = useQuery({
    queryKey: ['branches-list'],
    queryFn: () => get<{ id: string; name: string }[]>('/org/branches'),
    enabled: wantsBranches,
  });

  if (kinds.length === 0) return <Alert variant="destructive">{tr('billing', 'that_record_could_not_be_opened')}</Alert>;
  return <AuthorityChangeForm key={kinds.join(',')} target={target} kinds={kinds} branches={wantsBranches ? branches.data : undefined} returnTo={returnTo} />;
}

function AuthorityChangeForm({
  target,
  kinds,
  branches,
  returnTo,
}: {
  target: ChangeTarget;
  kinds: AuthorityKind[];
  branches?: { id: string; name: string }[];
  returnTo: string;
}) {
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [kind, setKind] = useState<AuthorityKind>(kinds[0]);
  const [value, setValue] = useState('');
  const [noLimit, setNoLimit] = useState(false);
  const [flag, setFlag] = useState(true);
  const lock = useBranchLock();
  const [branchId, setBranchId] = useState(target.branchId ?? lock.branchId);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const info = AUTHORITY_KIND_INFO[kind];

  const save = useMutation({
    mutationFn: async () => {
      const valuePct = info.value === 'pct' ? parsePercent(value) : null;
      const valueMinor = info.value === 'minor' && !noLimit ? parseAmountMinor(value) : null;
      if (info.value === 'pct' && (valuePct === null || valuePct < 0 || valuePct > 100)) throw new Error('Enter a percentage between 0 and 100');
      if (info.value === 'minor' && !noLimit && valueMinor === null) throw new Error('Enter an amount, or choose no limit');
      if (target.userIds && target.userIds.length > 0) {
        return post<{ pending: number }>('/accounts/authority/changes/bulk', {
          kind,
          userIds: target.userIds,
          valuePct,
          valueMinor,
          noLimit,
          reason: reason.trim(),
        }).then((result) => ({ status: result.pending > 0 ? 'pending' : 'active' }));
      }
      return post<{ status: string }>('/accounts/authority/changes', {
        kind,
        userId: target.userId ?? null,
        roleId: target.roleId ?? null,
        branchId: info.subject === 'hospital' && info.value !== 'flag' ? branchId || null : null,
        valuePct,
        valueMinor,
        noLimit: info.value === 'minor' ? noLimit : false,
        flag: info.value === 'flag' ? flag : null,
        reason: reason.trim(),
      });
    },
    onSuccess: (result) => {
      toast.success(result.status === 'pending' ? tr('billing', 'waiting_for_a_second_person') : tr('billing', 'saved'));
      void queryClient.invalidateQueries({ queryKey: ['authority-summary'] });
      void queryClient.invalidateQueries({ queryKey: ['authority-people'] });
      void queryClient.invalidateQueries({ queryKey: ['authority-person'] });
      void queryClient.invalidateQueries({ queryKey: ['authority-roles'] });
      navigate(returnTo, { replace: true });
    },
    onError: (caught) => setError(caught instanceof Error && !('status' in caught) ? caught.message : describeError(caught)),
  });

  const noLimitLabel = info.subject === 'hospital' ? 'No threshold: nothing needs a second person' : 'No limit';
  const ready = reason.trim().length >= 5;
  const dirty = !save.isSuccess && (value !== '' || noLimit || !flag || reason !== '' || kind !== kinds[0] || branchId !== (target.branchId ?? ''));

  return (
    <FormScreen
      title={target.title}
      description={tr('billing', 'every_change_keeps_its_reason_and_who_made_it')}
      eyebrow={tr('billing', 'financial_authority')}
      icon={ShieldCheck}
      backTo={returnTo}
      dirty={dirty}
      onSubmit={(event) => {
        event.preventDefault();
        if (!ready) return;
        setError(null);
        save.mutate();
      }}
    >
      <FormSection title={tr('billing', 'new_value')} icon={ShieldCheck}>
        {kinds.length > 1 && (
          <Field label={tr('billing', 'what_to_change')} className="sm:col-span-2">
            <Select value={kind} onChange={(event) => setKind(event.target.value as AuthorityKind)}>
              {kinds.map((option) => (
                <option key={option} value={option}>
                  {AUTHORITY_KIND_INFO[option].label}
                </option>
              ))}
            </Select>
          </Field>
        )}
        {info.value === 'flag' ? (
          <Checkbox checked={flag} onCheckedChange={setFlag} label={tr('billing', 'on')} />
        ) : (
          <>
            <Field
              label={info.value === 'pct' ? tr('billing', 'percentage') : tr('billing', 'amount')}
              hint={info.subject === 'hospital' ? tr('billing', 'no_threshold_nothing_needs_a_second_person_0') : undefined}
            >
              <Input inputMode="decimal" value={value} disabled={noLimit} onChange={(event) => setValue(event.target.value)} />
            </Field>
            {info.value === 'minor' && (info.unsetMeansNoLimit || info.subject === 'person') && (
              <div className="flex items-end">
                <Checkbox checked={noLimit} onCheckedChange={setNoLimit} label={noLimitLabel} />
              </div>
            )}
          </>
        )}
        {info.subject === 'hospital' && info.value !== 'flag' && branches && branches.length > 1 && (
          <Field label={tr('billing', 'branch')} hint={tr('billing', 'leave_on_every_branch_unless_one_branch_needs')}>
            <Select value={branchId} readOnly={lock.locked} onChange={(event) => setBranchId(event.target.value)}>
              <option value="">{tr('billing', 'every_branch')}</option>
              {branches.map((branch) => (
                <option key={branch.id} value={branch.id}>
                  {branch.name}
                </option>
              ))}
            </Select>
          </Field>
        )}
      </FormSection>

      <FormSection title={tr('billing', 'reason')} icon={MessageSquare} columns={1}>
        <Field label={tr('billing', 'reason')} required hint={tr('billing', 'at_least_5_characters_shown_in_the_history')}>
          <Textarea value={reason} onChange={(event) => setReason(event.target.value)} rows={3} />
        </Field>
      </FormSection>

      {error && <Alert variant="destructive">{error}</Alert>}

      <FormActions submitLabel={tr('billing', 'save_change')} loading={save.isPending} incomplete={!ready} cancelTo={returnTo} />
    </FormScreen>
  );
}
